import { useMemo, useRef, useState, useEffect } from "react";
import { useLocation } from "react-router";
import PageHeader from "../../components/PageHeader/PageHeader";
import CommonTable from "../../components/CommonTable/CommonTable";

import { FaEdit, FaTimes, FaTrash, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

import { addIcon, editIcon } from "../../assets/img/imgIndex";

import { get, post, put, deletes } from "../../Services/ApiHandler";
import { URLS } from "../../Services/Constant";

// ============================================================
// TIME OPTIONS
// ============================================================

const HOURS = Array.from({ length: 12 }, (_, index) =>
  String(index + 1).padStart(2, "0"),
);

const MINUTES = Array.from({ length: 60 }, (_, index) =>
  String(index).padStart(2, "0"),
);

const PERIODS = ["AM", "PM"];

// ============================================================
// TIME HELPERS
// ============================================================

const convert24To12 = (time = "00:00") => {
  if (!time) {
    return {
      hour: "12",
      minute: "00",
      period: "AM",
    };
  }

  const value = String(time).trim();

  // Already 12 hour format
  const twelveHourMatch = value.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);

  if (twelveHourMatch) {
    return {
      hour: String(twelveHourMatch[1]).padStart(2, "0"),
      minute: String(twelveHourMatch[2]).padStart(2, "0"),
      period: twelveHourMatch[3].toUpperCase(),
    };
  }

  // 24 hour format
  const twentyFourHourMatch = value.match(/^(\d{1,2}):(\d{2})$/);

  if (!twentyFourHourMatch) {
    return {
      hour: "12",
      minute: "00",
      period: "AM",
    };
  }

  let hour = Number(twentyFourHourMatch[1]);
  const minute = twentyFourHourMatch[2];

  let period = "AM";

  if (hour >= 12) {
    period = "PM";
  }

  if (hour === 0) {
    hour = 12;
  } else if (hour > 12) {
    hour -= 12;
  }

  return {
    hour: String(hour).padStart(2, "0"),
    minute,
    period,
  };
};

const convert12To24 = (time = "12:00 AM") => {
  const parsed = convert24To12(time);

  let hour = Number(parsed.hour);

  if (parsed.period === "AM") {
    if (hour === 12) {
      hour = 0;
    }
  } else {
    if (hour !== 12) {
      hour += 12;
    }
  }

  return `${String(hour).padStart(2, "0")}:${parsed.minute}`;
};

const formatTime = (hour, minute, period) => {
  return `${hour}:${minute} ${period}`;
};

const formatApiTime = (time) => {
  const parsed = convert24To12(time);

  return formatTime(parsed.hour, parsed.minute, parsed.period);
};

const timeToMinutes = (time) => {
  const apiTime = convert12To24(time);

  const [hour, minute] = apiTime.split(":").map(Number);

  return hour * 60 + minute;
};

// ============================================================
// TIME SELECT
// ============================================================

const TimeSelect = ({ value, onChange }) => {
  const parsed = convert24To12(value);

  const handleChange = (field, newValue) => {
    const updated = {
      ...parsed,
      [field]: newValue,
    };

    onChange(formatTime(updated.hour, updated.minute, updated.period));
  };

  return (
    <div className="time-select-wrapper">
      <select
        value={parsed.hour}
        onChange={(e) => handleChange("hour", e.target.value)}
        className="time-select"
      >
        {HOURS.map((hour) => (
          <option key={hour} value={hour}>
            {hour}
          </option>
        ))}
      </select>

      <span className="time-colon">:</span>

      <select
        value={parsed.minute}
        onChange={(e) => handleChange("minute", e.target.value)}
        className="time-select"
      >
        {MINUTES.map((minute) => (
          <option key={minute} value={minute}>
            {minute}
          </option>
        ))}
      </select>

      <select
        value={parsed.period}
        onChange={(e) => handleChange("period", e.target.value)}
        className="time-period-select"
      >
        {PERIODS.map((period) => (
          <option key={period} value={period}>
            {period}
          </option>
        ))}
      </select>
    </div>
  );
};

// ============================================================
// DEFAULT STATES
// ============================================================

const DEFAULT_MIN_CHARGE = {
  minKm: "",
  kmAmount: "",
  vehicleName: "Bike",
};

const DEFAULT_PER_KM = {
  km: "",
  kmPerPrice: "",
};

// ============================================================
// COMPONENT
// ============================================================

const DeliveryPricing = () => {
  const location = useLocation();

  // ==========================================================
  // MINIMUM CHARGE
  // ==========================================================

  const [isMinChargeEditing, setIsMinChargeEditing] = useState(false);

  const [minChargeDraft, setMinChargeDraft] = useState(DEFAULT_MIN_CHARGE);

  const [minChargeOriginal, setMinChargeOriginal] =
    useState(DEFAULT_MIN_CHARGE);

  // ==========================================================
  // PER KM
  // ==========================================================

  const [isPerKmEditing, setIsPerKmEditing] = useState(false);

  const [perKmDraft, setPerKmDraft] = useState(DEFAULT_PER_KM);

  const [perKmOriginal, setPerKmOriginal] = useState(DEFAULT_PER_KM);

  // ==========================================================
  // PEAK CHARGE
  // ==========================================================

  const [editingRowId, setEditingRowId] = useState(null);

  const [editingRowData, setEditingRowData] = useState({});

  const [originalRowData, setOriginalRowData] = useState({});

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const [page, setPage] = useState(location?.state?.page || 1);

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
  });

  const preSearchPageRef = useRef(page);

  // ==========================================================
  // DATA
  // ==========================================================

  const [peakChargeData, setPeakChargeData] = useState([]);

  // ==========================================================
  // FETCH DATA
  // ==========================================================

  const fetchData = async () => {
    try {
      const res = await get(URLS.DELIVERYPRICING);

      console.log("DELIVERY PRICING RESPONSE:", res);

      if (res?.status !== "success") {
        toast.error(res?.message || "Failed to fetch delivery pricing");
        return;
      }

      const data = res?.data || {};

      // ======================================================
      // MINIMUM DELIVERY CHARGE
      // ======================================================

      const deliveryMin = data?.delivery_min_charge?.values || {};

      const minChargeData = {
        minKm: deliveryMin?.min_km ?? "",
        kmAmount: deliveryMin?.min_km_amount ?? "",
        vehicleName: "Bike",
      };

      setMinChargeDraft(minChargeData);
      setMinChargeOriginal(minChargeData);

      // ======================================================
      // PER KM PRICE
      // ======================================================

      /*
       * Expected API example:
       *
       * km_charges: [
       *   {
       *     id: 1,
       *     values: {
       *       extra_km: 1,
       *       extra_km_amount: 20
       *     }
       *   }
       * ]
       *
       * Also supports:
       *
       * values: {
       *   km: 1,
       *   amount: 20
       * }
       */

      const kmCharges = Array.isArray(data?.km_charges) ? data.km_charges : [];

      if (kmCharges.length > 0) {
        const firstKmCharge = kmCharges[0];

        const values = firstKmCharge?.values || {};

        const perKmData = {
          km: values?.extra_km ?? values?.km ?? values?.from_km ?? "",
          kmPerPrice: values?.extra_km_amount ?? values?.amount ?? "",
        };

        setPerKmDraft(perKmData);
        setPerKmOriginal(perKmData);
      } else {
        setPerKmDraft(DEFAULT_PER_KM);
        setPerKmOriginal(DEFAULT_PER_KM);
      }

      // ======================================================
      // PEAK CHARGES
      // ======================================================

      const peakCharges = Array.isArray(data?.peak_charges)
        ? data.peak_charges
        : [];

      const formattedPeakData = peakCharges.flatMap((group, index) => {
        const values = group?.values;

        if (!values || typeof values !== "object" || Array.isArray(values)) {
          return [];
        }

        return [
          {
            id: group?.id ?? `peak-${index + 1}`,

            from_time: formatApiTime(values?.from_time || "00:00"),

            to_time: formatApiTime(values?.to_time || "01:00"),

            percentage: values?.percentage ?? "",

            isNew: false,
          },
        ];
      });

      setPeakChargeData(formattedPeakData);
    } catch (error) {
      console.error("Delivery pricing fetch error:", error);

      toast.error("Something went wrong while fetching pricing");
    }
  };

  // ==========================================================
  // INITIAL API CALL
  // ==========================================================

  useEffect(() => {
    fetchData();
  }, []);

  // ==========================================================
  // MINIMUM CHARGE EDIT
  // ==========================================================

  const handleMinChargeEdit = () => {
    setMinChargeOriginal({
      ...minChargeDraft,
    });

    setIsMinChargeEditing(true);
  };

  // ==========================================================
  // MINIMUM CHARGE CHANGE
  // ==========================================================

  const handleMinChargeChange = (e) => {
    const { name, value } = e.target;

    if (name === "minKm" || name === "kmAmount") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    setMinChargeDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==========================================================
  // MINIMUM CHARGE SAVE
  // ==========================================================

  const handleMinChargeSave = async (e) => {
    e?.preventDefault();

    if (!minChargeDraft.minKm) {
      toast.error("Please enter minimum KM");
      return;
    }

    if (!minChargeDraft.kmAmount) {
      toast.error("Please enter minimum KM amount");
      return;
    }

    try {
      const payload = {
        delivery_min_charge: {
          min_km: Number(minChargeDraft.minKm),
          min_km_amount: Number(minChargeDraft.kmAmount),
        },
      };

      console.log("MIN CHARGE PAYLOAD:", payload);

      const res = await post(URLS.DELIVERYPRICING, payload);

      if (res?.status !== "success") {
        toast.error(res?.message || "Failed to update minimum charge");
        return;
      }

      toast.success(res?.message || "Minimum charge updated successfully");

      setIsMinChargeEditing(false);

      await fetchData();
    } catch (error) {
      console.error("Minimum charge save error:", error);

      toast.error("Something went wrong while saving minimum charge");
    }
  };

  // ==========================================================
  // MINIMUM CHARGE CANCEL
  // ==========================================================

  const handleMinChargeCancel = () => {
    setMinChargeDraft({
      ...minChargeOriginal,
    });

    setIsMinChargeEditing(false);
  };

  // ==========================================================
  // PER KM EDIT
  // ==========================================================

  const handlePerKmEdit = () => {
    setPerKmOriginal({
      ...perKmDraft,
    });

    setIsPerKmEditing(true);
  };

  // ==========================================================
  // PER KM CHANGE
  // ==========================================================

  const handlePerKmChange = (e) => {
    const { name, value } = e.target;

    if (name === "km" || name === "kmPerPrice") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
        return;
      }
    }

    setPerKmDraft((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==========================================================
  // PER KM SAVE
  // ==========================================================

  const handlePerKmSave = async (e) => {
    e?.preventDefault();

    if (!perKmDraft.km) {
      toast.error("Please enter KM");
      return;
    }

    if (!perKmDraft.kmPerPrice) {
      toast.error("Please enter KM per price");
      return;
    }

    try {
      /*
       * Based on your existing API structure:
       *
       * km_charges: {
       *   extra_km: ...,
       *   extra_km_amount: ...
       * }
       */

      const payload = {
        km_charges: {
          extra_km: Number(perKmDraft.km),

          extra_km_amount: Number(perKmDraft.kmPerPrice),
        },
      };

      console.log("PER KM PAYLOAD:", payload);

      const res = await post(URLS.DELIVERYPRICING, payload);

      if (res?.status !== "success") {
        toast.error(res?.message || "Failed to update per KM price");
        return;
      }

      toast.success(res?.message || "Per KM price updated successfully");

      setIsPerKmEditing(false);

      await fetchData();
    } catch (error) {
      console.error("Per KM save error:", error);

      toast.error("Something went wrong while saving per KM price");
    }
  };

  // ==========================================================
  // PER KM CANCEL
  // ==========================================================

  const handlePerKmCancel = () => {
    setPerKmDraft({
      ...perKmOriginal,
    });

    setIsPerKmEditing(false);
  };

  // ==========================================================
  // ADD PEAK ROW
  // ==========================================================

  const handleAddRow = () => {
    if (editingRowId !== null) {
      toast.warning("Please save or cancel the current row first");
      return;
    }

    const newId = `new-${Date.now()}`;

    const newRow = {
      id: newId,
      from_time: "09:00 AM",
      to_time: "12:00 PM",
      percentage: "",
      isNew: true,
    };

    setPeakChargeData((prev) => [...prev, newRow]);

    setEditingRowId(newId);

    setEditingRowData({
      ...newRow,
    });

    setOriginalRowData({});
  };

  // ==========================================================
  // EDIT PEAK ROW
  // ==========================================================

  const handleEditRow = (row) => {
    if (editingRowId !== null && editingRowId !== row.id) {
      toast.warning("Please save or cancel the current row first");
      return;
    }

    setEditingRowId(row.id);

    setEditingRowData({
      ...row,
    });

    setOriginalRowData({
      ...row,
    });
  };

  // ==========================================================
  // PEAK INPUT CHANGE
  // ==========================================================

  const handleRowChange = (e) => {
    const { name, value } = e.target;

    if (name === "percentage") {
      if (value !== "" && !/^\d*\.?\d*$/.test(value)) {
        return;
      }

      if (Number(value) > 100) {
        return;
      }
    }

    setEditingRowData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ==========================================================
  // PEAK TIME CHANGE
  // ==========================================================

  const handlePeakTimeChange = (field, value) => {
    setEditingRowData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ==========================================================
  // SAVE PEAK ROW
  // ==========================================================

  const handleSaveRow = async (row) => {
    if (editingRowId === null) {
      return;
    }

    const fromTime = editingRowData.from_time;

    const toTime = editingRowData.to_time;

    const percentage = editingRowData.percentage;

    // --------------------------------------------------------
    // VALIDATION
    // --------------------------------------------------------

    if (!fromTime) {
      toast.error("Please select From Time");
      return;
    }

    if (!toTime) {
      toast.error("Please select To Time");
      return;
    }

    if (percentage === "" || percentage === null || percentage === undefined) {
      toast.error("Please enter percentage");
      return;
    }

    if (Number(percentage) < 0) {
      toast.error("Percentage cannot be negative");
      return;
    }

    if (Number(percentage) > 100) {
      toast.error("Percentage cannot be greater than 100");
      return;
    }

    const fromMinutes = timeToMinutes(fromTime);

    const toMinutes = timeToMinutes(toTime);

    if (fromMinutes >= toMinutes) {
      toast.error("To Time must be greater than From Time");
      return;
    }

    try {
      let res;

      // ======================================================
      // CREATE
      // ======================================================

      if (row?.isNew) {
        const payload = {
          peak_charges: {
            from_time: convert12To24(fromTime),

            to_time: convert12To24(toTime),

            percentage: Number(percentage),
          },
        };

        console.log("CREATE PEAK PAYLOAD:", payload);

        res = await post(URLS.DELIVERYPRICING, payload);
      }

      // ======================================================
      // UPDATE
      // ======================================================
      else {
        const payload = {
          id: row.id,

          peak_charges: {
            from_time: convert12To24(fromTime),

            to_time: convert12To24(toTime),

            percentage: Number(percentage),
          },
        };

        console.log("UPDATE PEAK PAYLOAD:", payload);

        res = await put(URLS.DELIVERYPRICING, payload);
      }

      if (res?.status !== "success") {
        toast.error(res?.message || "Failed to save peak charge");
        return;
      }

      toast.success(res?.message || "Peak charge saved successfully");

      setEditingRowId(null);
      setEditingRowData({});
      setOriginalRowData({});

      await fetchData();
    } catch (error) {
      console.error("Peak charge save error:", error);

      toast.error("Something went wrong while saving peak charge");
    }
  };

  // ==========================================================
  // CANCEL PEAK ROW
  // ==========================================================

  const handleCancelRow = (row) => {
    if (row?.isNew) {
      setPeakChargeData((prev) => prev.filter((item) => item.id !== row.id));
    } else {
      setPeakChargeData((prev) =>
        prev.map((item) =>
          item.id === row.id
            ? {
                ...originalRowData,
              }
            : item,
        ),
      );
    }

    setEditingRowId(null);
    setEditingRowData({});
    setOriginalRowData({});
  };

  // ==========================================================
  // DELETE PEAK ROW
  // ==========================================================

  const handleDeleteRow = async (row) => {
    if (!row?.id) {
      return;
    }

    try {
      const res = await deletes(`${URLS.DELIVERYPRICING}?id=${row.id}`);

      if (res?.status !== "success") {
        toast.error(res?.message || "Failed to delete peak charge");
        return;
      }

      toast.success(res?.message || "Deleted successfully");

      setEditingRowId(null);
      setEditingRowData({});
      setOriginalRowData({});

      await fetchData();
    } catch (error) {
      console.error("Delete peak charge error:", error);

      toast.error("Something went wrong while deleting");
    }
  };

  // ==========================================================
  // PEAK TABLE COLUMNS
  // ==========================================================

  const columnsTwo = useMemo(() => {
    return [
      {
        Header: "S.No",
        accessor: "sno",
        disableSortBy: true,
        width: "8%",

        Cell: ({ row }) => <span className="sno">{row.index + 1}</span>,
      },

      // ======================================================
      // FROM TIME
      // ======================================================

      {
        Header: "From Time",
        accessor: "from_time",
        width: "25%",

        Cell: ({ row }) => {
          const isEditing = editingRowId === row.original.id;

          if (isEditing) {
            return (
              <TimeSelect
                value={editingRowData.from_time || "09:00 AM"}
                onChange={(value) => handlePeakTimeChange("from_time", value)}
              />
            );
          }

          return <p className="mb-0">{row.original.from_time || "N/A"}</p>;
        },
      },

      // ======================================================
      // TO TIME
      // ======================================================

      {
        Header: "To Time",
        accessor: "to_time",
        width: "25%",

        Cell: ({ row }) => {
          const isEditing = editingRowId === row.original.id;

          if (isEditing) {
            return (
              <TimeSelect
                value={editingRowData.to_time || "12:00 PM"}
                onChange={(value) => handlePeakTimeChange("to_time", value)}
              />
            );
          }

          return <p className="mb-0">{row.original.to_time || "N/A"}</p>;
        },
      },

      // ======================================================
      // PERCENTAGE
      // ======================================================

      {
        Header: "Percentage",
        accessor: "percentage",
        width: "20%",

        Cell: ({ row }) => {
          const isEditing = editingRowId === row.original.id;

          if (isEditing) {
            return (
              <input
                type="text"
                name="percentage"
                value={editingRowData.percentage ?? ""}
                onChange={handleRowChange}
                className="table-input"
                placeholder="Percentage"
              />
            );
          }

          return <p className="mb-0">{row.original.percentage || "0"} %</p>;
        },
      },

      // ======================================================
      // ACTION
      // ======================================================

      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        width: "20%",

        Cell: ({ row }) => {
          const isEditing = editingRowId === row.original.id;

          if (isEditing) {
            return (
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                }}
              >
                <button
                  type="button"
                  className="action-btn"
                  title="Save"
                  onClick={() => handleSaveRow(row.original)}
                >
                  <FaSave />
                </button>

                <button
                  type="button"
                  className="action-btn"
                  title="Cancel"
                  onClick={() => handleCancelRow(row.original)}
                >
                  <FaTimes />
                </button>
              </div>
            );
          }

          return (
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <button
                type="button"
                className="action-btn"
                title="Edit"
                onClick={() => handleEditRow(row.original)}
              >
                <FaEdit />
              </button>

              <button
                type="button"
                className="action-btn"
                title="Delete"
                onClick={() => handleDeleteRow(row.original)}
              >
                <FaTrash color="red" />
              </button>
            </div>
          );
        },
      },
    ];
  }, [editingRowId, editingRowData, originalRowData]);

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div>
      <PageHeader title="Delivery Pricing" />

      {/* ======================================================
          MINIMUM DELIVERY CHARGE
      ====================================================== */}

      <div className="flexChanges">
        <p
          style={{
            fontSize: 20,
          }}
          className="section-title"
        >
          View Vehicle
        </p>

        {!isMinChargeEditing && (
          <div
            style={{
              cursor: "pointer",
            }}
            onClick={handleMinChargeEdit}
          >
            <img
              src={editIcon}
              alt="edit"
              style={{
                width: 18,
                height: 18,
                marginRight: 5,
                bottom: 2,
                position: "relative",
              }}
            />

            <span
              style={{
                fontSize: 18,
              }}
            >
              Edit
            </span>
          </div>
        )}
      </div>

      <form
        className="add-admin-form"
        onSubmit={handleMinChargeSave}
        noValidate
      >
        <div className="form-row">
          {/* MIN KM */}

          <div className="form-group">
            <label>Min. KM</label>

            <input
              type="text"
              name="minKm"
              value={minChargeDraft.minKm ?? ""}
              onChange={handleMinChargeChange}
              placeholder="Enter min KM"
              readOnly={!isMinChargeEditing}
              className={!isMinChargeEditing ? "readonly-input" : ""}
            />
          </div>

          {/* MIN KM AMOUNT */}

          <div className="form-group">
            <label>Min. KM Amount</label>

            <input
              type="text"
              name="kmAmount"
              value={minChargeDraft.kmAmount ?? ""}
              onChange={handleMinChargeChange}
              placeholder="Enter KM Amount"
              readOnly={!isMinChargeEditing}
              className={!isMinChargeEditing ? "readonly-input" : ""}
            />
          </div>

          {/* VEHICLE */}

          <div className="form-group">
            <label>Vehicle Name</label>

            <input
              type="text"
              value="Bike"
              readOnly
              className="readonly-input"
            />
          </div>

          {/* BUTTONS */}

          {isMinChargeEditing && (
            <div
              className="form-groups"
              style={{
                display: "flex",
                gap: 15,
                alignItems: "flex-end",
              }}
            >
              <button
                type="button"
                className="btn-edit"
                onClick={handleMinChargeCancel}
              >
                Cancel
              </button>

              <button type="submit" className="btn-save">
                Save
              </button>
            </div>
          )}
        </div>
      </form>

      {/* ======================================================
          PER KM PRICE
      ====================================================== */}

      <div className="flexChanges">
        <p
          style={{
            fontSize: 20,
          }}
          className="section-title"
        >
          Per Price
        </p>

        {!isPerKmEditing && (
          <div
            style={{
              cursor: "pointer",
            }}
            onClick={handlePerKmEdit}
          >
            <img
              src={editIcon}
              alt="edit"
              style={{
                width: 18,
                height: 18,
                marginRight: 5,
                bottom: 2,
                position: "relative",
              }}
            />

            <span
              style={{
                fontSize: 18,
              }}
            >
              Edit
            </span>
          </div>
        )}
      </div>

      <form className="add-admin-form" onSubmit={handlePerKmSave} noValidate>
        <div className="form-row">
          {/* KM */}

          <div className="form-group">
            <label>KM</label>

            <input
              type="text"
              name="km"
              value={perKmDraft.km ?? ""}
              onChange={handlePerKmChange}
              placeholder="Enter KM"
              readOnly={!isPerKmEditing}
              className={!isPerKmEditing ? "readonly-input" : ""}
            />
          </div>

          {/* PRICE */}

          <div className="form-group">
            <label>KM Per Price</label>

            <input
              type="text"
              name="kmPerPrice"
              value={perKmDraft.kmPerPrice ?? ""}
              onChange={handlePerKmChange}
              placeholder="Enter KM Price"
              readOnly={!isPerKmEditing}
              className={!isPerKmEditing ? "readonly-input" : ""}
            />
          </div>

          {/* BUTTONS */}

          {isPerKmEditing && (
            <div
              className="form-groups"
              style={{
                display: "flex",
                gap: 15,
                alignItems: "flex-end",
              }}
            >
              <button
                type="button"
                className="btn-edit"
                onClick={handlePerKmCancel}
              >
                Cancel
              </button>

              <button type="submit" className="btn-save">
                Save
              </button>
            </div>
          )}
        </div>
      </form>

      {/* ======================================================
          PEAK CHARGE
      ====================================================== */}

      <p
        style={{
          fontSize: 20,
        }}
        className="section-title"
      >
        Peak Charge
      </p>

      <div className="flexChanges">
        <div className="d-flex gap-5 mb-5" />

        {/* ADD */}

        <div
          style={{
            cursor: editingRowId !== null ? "not-allowed" : "pointer",

            opacity: editingRowId !== null ? 0.5 : 1,
          }}
          onClick={() => {
            if (editingRowId === null) {
              handleAddRow();
            }
          }}
        >
          <img
            src={addIcon}
            alt="add"
            style={{
              width: 18,
              height: 18,
              marginRight: 5,
              bottom: 2,
              position: "relative",
            }}
          />

          <span
            style={{
              fontSize: 18,
            }}
          >
            Add
          </span>
        </div>
      </div>

      {/* ======================================================
          PEAK TABLE
      ====================================================== */}

      <CommonTable
        columns={columnsTwo}
        data={peakChargeData}
        showPagination={false}
      />
    </div>
  );
};

export default DeliveryPricing;
